import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  Users, Plus, ArrowLeft, Building2, UserCircle, CheckCircle2, 
  Loader2, Trash2, Edit, Search, X, Briefcase
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOrganizationHierarchy, DbTeam, DbDepartment } from '@/lib/useOrganizationHierarchy';
import { useUsers } from '@/lib/useUsers';
import { useToast } from '@/lib/toast';

export function TeamManagementPage({ 
  onBack, 
  initialTab = 'teams',
}: { 
  onBack: () => void, 
  initialTab?: string,
}) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const toast = useToast();

  const {
    teams,
    organizations,
    departments,
    loading,
    refresh,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    addTeam,
    updateTeam,
    deleteTeam,
    getTeamMembers,
    addTeamMember,
    removeTeamMember
  } = useOrganizationHierarchy();

  const { profiles } = useUsers();

  const [search, setSearch] = useState('');

  // Team Create / Edit Modal State
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<DbTeam | null>(null);
  const [teamFormOrgId, setTeamFormOrgId] = useState('');
  const [teamFormDeptId, setTeamFormDeptId] = useState('');
  const [teamFormName, setTeamFormName] = useState('');
  const [teamFormCode, setTeamFormCode] = useState('');
  const [teamFormDesc, setTeamFormDesc] = useState('');
  const [teamFormLeadId, setTeamFormLeadId] = useState('');
  const [teamFormDefaultRole, setTeamFormDefaultRole] = useState('Technician');
  const [teamFormSlaPolicy, setTeamFormSlaPolicy] = useState('Standard 4h Response');
  const [teamFormStatus, setTeamFormStatus] = useState('Active');
  const [isSavingTeam, setIsSavingTeam] = useState(false);

  // Department Create / Edit Modal State
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<DbDepartment | null>(null);
  const [deptFormOrgId, setDeptFormOrgId] = useState('');
  const [deptFormName, setDeptFormName] = useState('');
  const [deptFormCode, setDeptFormCode] = useState('');
  const [deptFormDesc, setDeptFormDesc] = useState('');
  const [deptFormHeadId, setDeptFormHeadId] = useState('');
  const [deptFormStatus, setDeptFormStatus] = useState('Active');
  const [isSavingDept, setIsSavingDept] = useState(false);

  // Team Members Modal State (Req 13)
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [selectedTeamForMembers, setSelectedTeamForMembers] = useState<DbTeam | null>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [newMemberUserId, setNewMemberUserId] = useState('');
  const [newMemberIsPrimary, setNewMemberIsPrimary] = useState(false);
  const [newMemberRole, setNewMemberRole] = useState('MEMBER');
  const [isAddingMember, setIsAddingMember] = useState(false);

  // Open Team Create Modal
  const handleOpenCreateTeam = () => {
    setEditingTeam(null);
    setTeamFormOrgId(organizations[0]?.id || '');
    setTeamFormDeptId('');
    setTeamFormName('');
    setTeamFormCode('');
    setTeamFormDesc('');
    setTeamFormLeadId('');
    setTeamFormDefaultRole('Technician');
    setTeamFormSlaPolicy('Standard 4h Response');
    setTeamFormStatus('Active');
    setIsTeamModalOpen(true);
  };

  // Open Team Edit Modal
  const handleOpenEditTeam = (team: DbTeam) => {
    setEditingTeam(team);
    setTeamFormOrgId(team.organization_id || organizations[0]?.id || '');
    setTeamFormDeptId(team.department_id || '');
    setTeamFormName(team.name);
    setTeamFormCode(team.code || '');
    setTeamFormDesc(team.description || '');
    setTeamFormLeadId(team.lead_id || '');
    setTeamFormDefaultRole(team.default_role || 'Technician');
    setTeamFormSlaPolicy(team.sla_policy || 'Standard 4h Response');
    setTeamFormStatus(team.status || 'Active');
    setIsTeamModalOpen(true);
  };

  // Save Team (Create or Update)
  const handleSaveTeam = async () => {
    if (!teamFormName.trim() || !teamFormOrgId) {
      toast.push('Please provide a team name and select an organization.');
      return;
    }

    try {
      setIsSavingTeam(true);
      if (editingTeam) {
        await updateTeam(editingTeam.id, teamFormName.trim(), teamFormDeptId || null, {
          code: teamFormCode.trim() || null,
          description: teamFormDesc.trim() || null,
          lead_id: teamFormLeadId || null,
          default_role: teamFormDefaultRole,
          sla_policy: teamFormSlaPolicy,
          status: teamFormStatus
        });
        toast.push('Team updated successfully.');
      } else {
        await addTeam(teamFormOrgId, teamFormDeptId || null, teamFormName.trim(), {
          code: teamFormCode.trim() || null,
          description: teamFormDesc.trim() || null,
          lead_id: teamFormLeadId || null,
          default_role: teamFormDefaultRole,
          sla_policy: teamFormSlaPolicy,
          status: teamFormStatus
        });
        toast.push('Team created successfully.');
      }
      setIsTeamModalOpen(false);
      refresh();
    } catch (e: any) {
      toast.push(`Failed to save team: ${e.message}`);
    } finally {
      setIsSavingTeam(false);
    }
  };

  // Delete Team
  const handleDeleteTeam = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this functional team?')) {
      try {
        await deleteTeam(id);
        toast.push('Team removed.');
        refresh();
      } catch (e: any) {
        toast.push(`Failed to delete team: ${e.message}`);
      }
    }
  };

  // Open Department Create Modal
  const handleOpenCreateDept = () => {
    setEditingDept(null);
    setDeptFormOrgId(organizations[0]?.id || '');
    setDeptFormName('');
    setDeptFormCode('');
    setDeptFormDesc('');
    setDeptFormHeadId('');
    setDeptFormStatus('Active');
    setIsDeptModalOpen(true);
  };

  // Open Department Edit Modal
  const handleOpenEditDept = (dept: DbDepartment) => {
    setEditingDept(dept);
    setDeptFormOrgId(dept.organization_id || organizations[0]?.id || '');
    setDeptFormName(dept.name);
    setDeptFormCode(dept.code || '');
    setDeptFormDesc(dept.description || '');
    setDeptFormHeadId(dept.head_id || '');
    setDeptFormStatus(dept.status || 'Active');
    setIsDeptModalOpen(true);
  };

  // Save Department (Create or Update)
  const handleSaveDepartment = async () => {
    if (!deptFormName.trim() || !deptFormOrgId) {
      toast.push('Please provide a department name and select an organization.');
      return;
    }

    try {
      setIsSavingDept(true);
      if (editingDept) {
        await updateDepartment(editingDept.id, deptFormName.trim(), {
          code: deptFormCode.trim() || null,
          description: deptFormDesc.trim() || null,
          head_id: deptFormHeadId || null,
          status: deptFormStatus
        });
        toast.push('Department updated successfully.');
      } else {
        await addDepartment(deptFormOrgId, deptFormName.trim(), {
          code: deptFormCode.trim() || null,
          description: deptFormDesc.trim() || null,
          head_id: deptFormHeadId || null,
          status: deptFormStatus
        });
        toast.push('Department created successfully.');
      }
      setIsDeptModalOpen(false);
      refresh();
    } catch (e: any) {
      toast.push(`Failed to save department: ${e.message}`);
    } finally {
      setIsSavingDept(false);
    }
  };

  // Delete Department
  const handleDeleteDept = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this department? Associated teams may be unlinked.')) {
      try {
        await deleteDepartment(id);
        toast.push('Department removed.');
        refresh();
      } catch (e: any) {
        toast.push(`Failed to delete department: ${e.message}`);
      }
    }
  };

  // Open Manage Members Modal
  const handleOpenMembersModal = async (team: DbTeam) => {
    setSelectedTeamForMembers(team);
    setTeamMembers([]);
    setNewMemberUserId('');
    setNewMemberIsPrimary(false);
    setNewMemberRole('MEMBER');
    setIsMembersModalOpen(true);

    try {
      setLoadingMembers(true);
      const members = await getTeamMembers(team.id);
      setTeamMembers(members || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Add Team Member (Primary or Secondary)
  const handleAddMember = async () => {
    if (!selectedTeamForMembers || !newMemberUserId) return;
    try {
      setIsAddingMember(true);
      await addTeamMember(selectedTeamForMembers.id, newMemberUserId, newMemberIsPrimary, newMemberRole);
      toast.push('Member added to team.');
      const members = await getTeamMembers(selectedTeamForMembers.id);
      setTeamMembers(members || []);
      setNewMemberUserId('');
      refresh();
    } catch (e: any) {
      toast.push(`Failed to add member: ${e.message}`);
    } finally {
      setIsAddingMember(false);
    }
  };

  // Remove Team Member
  const handleRemoveMember = async (userId: string) => {
    if (!selectedTeamForMembers) return;
    try {
      await removeTeamMember(selectedTeamForMembers.id, userId);
      toast.push('Member removed from team.');
      setTeamMembers(teamMembers.filter(m => m.user_id !== userId));
      refresh();
    } catch (e: any) {
      toast.push(`Failed to remove member: ${e.message}`);
    }
  };

  // Filtered Teams
  const filteredTeams = useMemo(() => {
    const q = search.toLowerCase();
    return teams.filter(t => 
      !q ||
      t.name.toLowerCase().includes(q) ||
      (t.code && t.code.toLowerCase().includes(q)) ||
      (t.description && t.description.toLowerCase().includes(q))
    );
  }, [teams, search]);

  // Filtered Departments
  const filteredDepartments = useMemo(() => {
    const q = search.toLowerCase();
    return departments.filter(d => 
      !q ||
      d.name.toLowerCase().includes(q) ||
      (d.code && d.code.toLowerCase().includes(q)) ||
      (d.description && d.description.toLowerCase().includes(q))
    );
  }, [departments, search]);

  if (loading && teams.length === 0 && departments.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col relative">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-card/60 px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-lg font-semibold tracking-tight">Team & Department Management</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              Configure organizational departments, functional teams, and multi-tier team memberships.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {activeTab === 'teams' && (
            <button 
              onClick={handleOpenCreateTeam}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 shadow-sm"
            >
              <Plus className="h-4 w-4" /> Create Team
            </button>
          )}
          {activeTab === 'departments' && (
            <button 
              onClick={handleOpenCreateDept}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 shadow-sm"
            >
              <Plus className="h-4 w-4" /> Create Department
            </button>
          )}
        </div>
      </div>

      {/* Main Body */}
      <div className="flex-1 overflow-y-auto bg-muted/10 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto w-full max-w-7xl space-y-6">
          
          {/* Navigation Tabs */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-2">
            <div className="flex items-center gap-2">
              {[
                { id: 'teams', label: 'Functional Teams', icon: Users, count: teams.length },
                { id: 'departments', label: 'Departments', icon: Building2, count: departments.length },
                { id: 'memberships', label: 'Team Members Directory', icon: Briefcase, count: profiles.length },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                    activeTab === tab.id ? "bg-card shadow-sm border border-border text-foreground" : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                  <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground font-semibold">
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative max-w-xs w-full">
              <input 
                type="text" 
                placeholder="Search..." 
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full rounded-lg border border-input bg-background pl-9 pr-3 py-1.5 text-xs outline-none transition focus:border-primary"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            </div>
          </div>

          {/* TEAMS TAB (Req 11: Name, Code, Dept, Desc, Lead, Default Role, SLA Policy, Status) */}
          {activeTab === 'teams' && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredTeams.map((team) => {
                  const org = organizations.find(o => o.id === team.organization_id);
                  const dept = departments.find(d => d.id === team.department_id);
                  const lead = profiles.find(p => p.id === team.lead_id);
                  const memberCount = profiles.filter(p => p.team_id === team.id).length;
                  const isActive = (team.status || 'Active').toLowerCase() === 'active';

                  return (
                    <div key={team.id} className="relative flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md hover:border-primary/30">
                      <div>
                        {/* Header: Title & Code */}
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500 shrink-0">
                              <Users className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="font-display font-semibold text-foreground flex items-center gap-2">
                                {team.name}
                                {team.code && (
                                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                                    {team.code}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <Building2 className="h-3 w-3" /> {org?.organization_name || 'Rezilyens Corp'} {dept ? `• ${dept.name}` : ''}
                              </div>
                            </div>
                          </div>
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
                            isActive ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"
                          )}>
                            {team.status || 'Active'}
                          </span>
                        </div>

                        {/* Description */}
                        {team.description && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{team.description}</p>
                        )}

                        {/* Metadata Grid (Req 11) */}
                        <div className="grid grid-cols-2 gap-2 mt-4 text-xs bg-muted/20 p-2.5 rounded-lg border border-border">
                          <div>
                            <span className="text-muted-foreground text-[10px] uppercase font-semibold block">Team Lead</span>
                            <span className="font-medium text-foreground truncate block flex items-center gap-1">
                              <UserCircle className="h-3.5 w-3.5 text-primary shrink-0" />
                              {lead?.full_name || 'Unassigned'}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground text-[10px] uppercase font-semibold block">Members</span>
                            <span className="font-medium text-foreground">{memberCount} assigned</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground text-[10px] uppercase font-semibold block">Default Role</span>
                            <span className="font-medium text-foreground">{team.default_role || 'Technician'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground text-[10px] uppercase font-semibold block">SLA Policy</span>
                            <span className="font-medium text-foreground truncate block">{team.sla_policy || 'Standard 4h'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Card Actions */}
                      <div className="mt-4 flex items-center justify-between pt-3 border-t border-border">
                        <button 
                          onClick={() => handleOpenMembersModal(team)}
                          className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                        >
                          <Users className="h-3.5 w-3.5" /> Manage Members
                        </button>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleOpenEditTeam(team)}
                            className="p-1 text-muted-foreground hover:text-foreground transition rounded hover:bg-muted"
                            title="Edit Team"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteTeam(team.id)}
                            className="p-1 text-muted-foreground hover:text-rose-500 transition rounded hover:bg-muted"
                            title="Delete Team"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {filteredTeams.length === 0 && (
                  <div className="col-span-full py-12 text-center text-sm text-muted-foreground bg-card border border-dashed border-border rounded-xl">
                    No functional teams found matching your query.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DEPARTMENTS TAB (Req 12: Name, Code, Desc, Head, Status) */}
          {activeTab === 'departments' && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredDepartments.map((dept) => {
                  const org = organizations.find(o => o.id === dept.organization_id);
                  const head = profiles.find(p => p.id === dept.head_id);
                  const deptTeams = teams.filter(t => t.department_id === dept.id);
                  const isActive = (dept.status || 'Active').toLowerCase() === 'active';

                  return (
                    <div key={dept.id} className="relative flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md hover:border-primary/30">
                      <div>
                        {/* Header: Title & Code */}
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500 shrink-0">
                              <Building2 className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="font-display font-semibold text-foreground flex items-center gap-2">
                                {dept.name}
                                {dept.code && (
                                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                                    {dept.code}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {org?.organization_name || 'Rezilyens Corp'}
                              </div>
                            </div>
                          </div>
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
                            isActive ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"
                          )}>
                            {dept.status || 'Active'}
                          </span>
                        </div>

                        {/* Description */}
                        {dept.description && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{dept.description}</p>
                        )}

                        {/* Metadata Grid (Req 12) */}
                        <div className="grid grid-cols-2 gap-2 mt-4 text-xs bg-muted/20 p-2.5 rounded-lg border border-border">
                          <div>
                            <span className="text-muted-foreground text-[10px] uppercase font-semibold block">Department Head</span>
                            <span className="font-medium text-foreground truncate block flex items-center gap-1">
                              <UserCircle className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                              {head?.full_name || 'Unassigned'}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground text-[10px] uppercase font-semibold block">Sub-Teams</span>
                            <span className="font-medium text-foreground">{deptTeams.length} functional teams</span>
                          </div>
                        </div>
                      </div>

                      {/* Card Actions */}
                      <div className="mt-4 flex items-center justify-end gap-2 pt-3 border-t border-border">
                        <button 
                          onClick={() => handleOpenEditDept(dept)}
                          className="p-1 text-muted-foreground hover:text-foreground transition rounded hover:bg-muted"
                          title="Edit Department"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteDept(dept.id)}
                          className="p-1 text-muted-foreground hover:text-rose-500 transition rounded hover:bg-muted"
                          title="Delete Department"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {filteredDepartments.length === 0 && (
                  <div className="col-span-full py-12 text-center text-sm text-muted-foreground bg-card border border-dashed border-border rounded-xl">
                    No organizational departments found matching your query.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TEAM MEMBERSHIPS DIRECTORY (Req 13: Primary & Secondary Teams) */}
          {activeTab === 'memberships' && (
            <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-muted/50 text-muted-foreground border-b border-border">
                  <tr>
                    <th className="px-5 py-3.5 font-semibold uppercase tracking-wider text-[11px]">User</th>
                    <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Role</th>
                    <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Department</th>
                    <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Primary Team</th>
                    <th className="px-4 py-3.5 font-semibold uppercase tracking-wider text-[11px]">Status</th>
                    <th className="px-5 py-3.5 text-right font-semibold uppercase tracking-wider text-[11px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {profiles.map((u) => {
                    const primaryTeam = teams.find(t => t.id === u.team_id);
                    const dept = departments.find(d => d.id === u.department_id) || (u.department ? { name: u.department } : null);

                    return (
                      <tr key={u.id} className="transition hover:bg-muted/30">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs shrink-0">
                              {(u.full_name || u.email || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-foreground">{u.full_name || 'Unnamed'}</div>
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-xs font-semibold text-primary">{u.role}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-xs text-foreground">{dept?.name || '—'}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          {primaryTeam ? (
                            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                              <CheckCircle2 className="h-3 w-3" /> {primaryTeam.name}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">Unassigned</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-500/10 text-emerald-500">
                            {u.status || 'Active'}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          {primaryTeam && (
                            <button 
                              onClick={() => handleOpenMembersModal(primaryTeam)}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              Manage in Team
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>

      {/* CREATE / EDIT TEAM MODAL (Req 11) */}
      {isTeamModalOpen && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsTeamModalOpen(false);
          }}
        >
          <div className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
            <div className="flex items-start justify-between border-b border-border p-6 flex-shrink-0 bg-card">
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground">
                  {editingTeam ? 'Edit Functional Team' : 'Create Functional Team'}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Configure functional team specifications and assignment rules.</p>
              </div>
              <button 
                type="button"
                onClick={() => setIsTeamModalOpen(false)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition -mr-2 -mt-2"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Organization */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organization *</label>
                <select 
                  value={teamFormOrgId} 
                  onChange={e => {
                    setTeamFormOrgId(e.target.value);
                    setTeamFormDeptId('');
                  }}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                >
                  {organizations.map(o => (
                    <option key={o.id} value={o.id}>{o.organization_name}</option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Department</label>
                <select 
                  value={teamFormDeptId} 
                  onChange={e => setTeamFormDeptId(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                >
                  <option value="">No Department Attached</option>
                  {departments.filter(d => d.organization_id === teamFormOrgId).map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              {/* Team Name & Code */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Team Name *</label>
                  <input 
                    value={teamFormName}
                    onChange={e => setTeamFormName(e.target.value)}
                    placeholder="e.g. Detection Engineers"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Team Code</label>
                  <input 
                    value={teamFormCode}
                    onChange={e => setTeamFormCode(e.target.value)}
                    placeholder="e.g. DET-ENG"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</label>
                <textarea 
                  value={teamFormDesc}
                  onChange={e => setTeamFormDesc(e.target.value)}
                  placeholder="Team responsibilities and scope..."
                  rows={2}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary resize-none" 
                />
              </div>

              {/* Team Lead */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Team Lead</label>
                <select 
                  value={teamFormLeadId} 
                  onChange={e => setTeamFormLeadId(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                >
                  <option value="">Unassigned</option>
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>{p.full_name} ({p.role})</option>
                  ))}
                </select>
              </div>

              {/* Default Role & SLA Policy */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Default Role</label>
                  <select 
                    value={teamFormDefaultRole} 
                    onChange={e => setTeamFormDefaultRole(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  >
                    <option value="Technician">Technician</option>
                    <option value="SOC User">SOC User</option>
                    <option value="SOC Technician">SOC Technician</option>
                    <option value="Team Lead">Team Lead</option>
                    <option value="User">User</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">SLA Policy</label>
                  <select 
                    value={teamFormSlaPolicy} 
                    onChange={e => setTeamFormSlaPolicy(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  >
                    <option value="Critical 15m Response">Critical (15m)</option>
                    <option value="High 1h Response">High (1h)</option>
                    <option value="Standard 4h Response">Standard (4h)</option>
                    <option value="Low 24h Response">Low (24h)</option>
                  </select>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
                <select 
                  value={teamFormStatus} 
                  onChange={e => setTeamFormStatus(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-border px-6 py-4 bg-muted/20 flex-shrink-0">
              <button 
                type="button"
                onClick={() => setIsTeamModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition"
              >
                Cancel
              </button>
              <button 
                type="button"
                disabled={isSavingTeam || !teamFormName.trim()}
                onClick={handleSaveTeam}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
              >
                {isSavingTeam && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingTeam ? 'Save Changes' : 'Create Team'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CREATE / EDIT DEPARTMENT MODAL (Req 12) */}
      {isDeptModalOpen && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsDeptModalOpen(false);
          }}
        >
          <div className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
            <div className="flex items-start justify-between border-b border-border p-6 flex-shrink-0 bg-card">
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground">
                  {editingDept ? 'Edit Department' : 'Create Department'}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Configure organizational departments and leadership.</p>
              </div>
              <button 
                type="button"
                onClick={() => setIsDeptModalOpen(false)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition -mr-2 -mt-2"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Organization */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organization *</label>
                <select 
                  value={deptFormOrgId} 
                  onChange={e => setDeptFormOrgId(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                >
                  {organizations.map(o => (
                    <option key={o.id} value={o.id}>{o.organization_name}</option>
                  ))}
                </select>
              </div>

              {/* Department Name & Code */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Department Name *</label>
                  <input 
                    value={deptFormName}
                    onChange={e => setDeptFormName(e.target.value)}
                    placeholder="e.g. Information Technology"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Department Code</label>
                  <input 
                    value={deptFormCode}
                    onChange={e => setDeptFormCode(e.target.value)}
                    placeholder="e.g. IT-OPS"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</label>
                <textarea 
                  value={deptFormDesc}
                  onChange={e => setDeptFormDesc(e.target.value)}
                  placeholder="Department scope and functions..."
                  rows={2}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary resize-none" 
                />
              </div>

              {/* Department Head */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Department Head</label>
                <select 
                  value={deptFormHeadId} 
                  onChange={e => setDeptFormHeadId(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                >
                  <option value="">Unassigned</option>
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>{p.full_name} ({p.role})</option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
                <select 
                  value={deptFormStatus} 
                  onChange={e => setDeptFormStatus(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-border px-6 py-4 bg-muted/20 flex-shrink-0">
              <button 
                type="button"
                onClick={() => setIsDeptModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition"
              >
                Cancel
              </button>
              <button 
                type="button"
                disabled={isSavingDept || !deptFormName.trim()}
                onClick={handleSaveDepartment}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
              >
                {isSavingDept && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingDept ? 'Save Changes' : 'Create Department'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* TEAM MEMBERSHIP MODAL (Req 13: Add/Remove Members, Primary & Secondary) */}
      {isMembersModalOpen && selectedTeamForMembers && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsMembersModalOpen(false);
          }}
        >
          <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150">
            <div className="flex items-start justify-between border-b border-border p-6 flex-shrink-0 bg-card">
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Manage Members: {selectedTeamForMembers.name}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Assign primary or secondary functional team memberships.
                </p>
              </div>
              <button 
                type="button"
                onClick={() => setIsMembersModalOpen(false)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition -mr-2 -mt-2"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {/* Add Member Form */}
              <div className="p-4 bg-muted/20 border border-border rounded-xl space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Add Member to Team</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase font-semibold text-muted-foreground block mb-1">Select User</label>
                    <select
                      value={newMemberUserId}
                      onChange={e => setNewMemberUserId(e.target.value)}
                      className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-xs outline-none transition focus:border-primary"
                    >
                      <option value="">Select a user to assign...</option>
                      {profiles
                        .filter(p => !teamMembers.some(m => m.user_id === p.id))
                        .map(p => (
                          <option key={p.id} value={p.id}>{p.full_name} ({p.role}) - {p.email}</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-semibold text-muted-foreground block mb-1">Role in Team</label>
                    <select
                      value={newMemberRole}
                      onChange={e => setNewMemberRole(e.target.value)}
                      className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-xs outline-none transition focus:border-primary"
                    >
                      <option value="MEMBER">Member</option>
                      <option value="LEAD">Lead</option>
                      <option value="BACKUP">Backup</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                    <input 
                      type="checkbox"
                      checked={newMemberIsPrimary}
                      onChange={e => setNewMemberIsPrimary(e.target.checked)}
                      className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                    />
                    Designate as Primary Team for this user
                  </label>

                  <button
                    type="button"
                    disabled={isAddingMember || !newMemberUserId}
                    onClick={handleAddMember}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 transition"
                  >
                    {isAddingMember ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    Add Member
                  </button>
                </div>
              </div>

              {/* Members List */}
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current Team Members ({teamMembers.length})</h3>
                {loadingMembers ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-primary" />
                    Loading members...
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="py-6 text-center text-xs text-muted-foreground bg-card border border-dashed border-border rounded-xl">
                    No members assigned to this team yet. Add one above.
                  </div>
                ) : (
                  <div className="divide-y divide-border border border-border rounded-xl overflow-hidden">
                    {teamMembers.map((member) => {
                      const profile = member.profiles || profiles.find(p => p.id === member.user_id);
                      const isPrimary = member.is_primary;

                      return (
                        <div key={member.id || member.user_id} className="flex items-center justify-between p-3 bg-card hover:bg-muted/20 transition">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs shrink-0">
                              {(profile?.full_name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-foreground flex items-center gap-2">
                                {profile?.full_name || 'Unnamed User'}
                                {isPrimary ? (
                                  <span className="rounded bg-emerald-500/10 text-emerald-600 px-1.5 py-0.2 text-[10px] font-bold">
                                    Primary Team
                                  </span>
                                ) : (
                                  <span className="rounded bg-muted text-muted-foreground px-1.5 py-0.2 text-[10px] font-medium">
                                    Secondary Team
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-muted-foreground">{profile?.email} • {member.role_in_team || 'MEMBER'}</div>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="text-xs text-rose-500 hover:text-rose-600 hover:underline p-1 font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-end bg-muted/20 flex-shrink-0">
              <button 
                type="button"
                onClick={() => setIsMembersModalOpen(false)}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
